import React, { useEffect, useState, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Plus, Pencil, Trash2, Loader2, Instagram, Upload, X } from 'lucide-react';
import {
  apiGetCreators,
  apiCreateCreator,
  apiUpdateCreator,
  apiDeleteCreator,
  apiUploadImage,
  resolveImageUrl,
  formatApiError,
} from '../../lib/api';
import { toast } from 'sonner';

const emptyForm = {
  name: '',
  username: '',
  category: '',
  instagram_url: '',
  followers: '',
  description: '',
  profile_pic: '',
};

export default function CreatorsAdmin() {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const loadCreators = async () => {
    setLoading(true);
    try {
      const { data } = await apiGetCreators();
      setCreators(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Failed to load creators');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCreators();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (creator) => {
    setEditingId(creator.id);
    setForm({
      name: creator.name,
      username: creator.username,
      category: creator.category,
      instagram_url: creator.instagram_url,
      followers: creator.followers,
      description: creator.description,
      profile_pic: creator.profile_pic || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        await apiUpdateCreator(editingId, form);
        toast.success('Creator updated successfully');
      } else {
        await apiCreateCreator(form);
        toast.success('Creator added successfully');
      }
      setDialogOpen(false);
      loadCreators();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Failed to save creator');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this creator?')) return;
    try {
      await apiDeleteCreator(id);
      toast.success('Creator deleted');
      loadCreators();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Failed to delete');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }
    setUploading(true);
    try {
      const { data } = await apiUploadImage(file);
      setForm((f) => ({ ...f, profile_pic: data.url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="creators-admin">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Exclusive Creators</h2>
          <p className="text-gray-500">Manage your exclusive talent roster</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openCreate}
              className="bg-gradient-to-r from-orange-600 to-rose-600 hover:from-orange-700 hover:to-rose-700 text-white font-bold"
              data-testid="add-creator-button"
            >
              <Plus size={18} className="mr-2" />
              Add Creator
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Creator' : 'Add New Creator'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    data-testid="creator-form-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="username">Instagram Username *</Label>
                  <Input
                    id="username"
                    data-testid="creator-form-username"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    placeholder="e.g. techie007.dev"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Input
                    id="category"
                    data-testid="creator-form-category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="e.g. Finance, Tech, Lifestyle"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="followers">Followers *</Label>
                  <Input
                    id="followers"
                    data-testid="creator-form-followers"
                    value={form.followers}
                    onChange={(e) => setForm({ ...form, followers: e.target.value })}
                    placeholder="e.g. 500K+"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="instagram_url">Instagram URL *</Label>
                <Input
                  id="instagram_url"
                  data-testid="creator-form-instagram-url"
                  value={form.instagram_url}
                  onChange={(e) => setForm({ ...form, instagram_url: e.target.value })}
                  placeholder="https://www.instagram.com/..."
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Profile Picture</Label>
                <div className="flex items-start gap-4">
                  {/* Preview */}
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-orange-400 to-rose-500 flex-shrink-0 flex items-center justify-center ring-2 ring-orange-200">
                    {form.profile_pic ? (
                      <img
                        src={resolveImageUrl(form.profile_pic)}
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="text-white font-bold text-2xl">
                        {form.name?.charAt(0) || '?'}
                      </span>
                    )}
                  </div>

                  <div className="flex-1 space-y-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="creator-form-file-input"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="bg-gradient-to-r from-orange-600 to-rose-600 hover:from-orange-700 hover:to-rose-700 text-white"
                        data-testid="creator-form-upload-button"
                      >
                        {uploading ? (
                          <>
                            <Loader2 size={16} className="mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload size={16} className="mr-2" />
                            Upload Image
                          </>
                        )}
                      </Button>
                      {form.profile_pic && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setForm({ ...form, profile_pic: '' })}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          data-testid="creator-form-remove-image"
                        >
                          <X size={16} />
                        </Button>
                      )}
                    </div>
                    <Input
                      id="profile_pic"
                      data-testid="creator-form-profile-pic"
                      value={form.profile_pic}
                      onChange={(e) => setForm({ ...form, profile_pic: e.target.value })}
                      placeholder="Or paste an image URL"
                      className="text-xs"
                    />
                    <p className="text-xs text-gray-500">JPG, PNG, WebP up to 5MB. Or paste a URL above.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  data-testid="creator-form-description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  required
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  data-testid="creator-form-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-gradient-to-r from-orange-600 to-rose-600 hover:from-orange-700 hover:to-rose-700 text-white"
                  data-testid="creator-form-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : editingId ? (
                    'Update Creator'
                  ) : (
                    'Add Creator'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-orange-600" size={32} />
        </div>
      ) : creators.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No creators yet. Click "Add Creator" to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {creators.map((creator) => (
            <Card key={creator.id} className="border-2 border-gray-100 hover:border-orange-300 transition-all" data-testid={`creator-card-${creator.id}`}>
              <CardHeader className="space-y-2">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{creator.name}</CardTitle>
                    <CardDescription className="flex items-center gap-1">
                      <Instagram size={14} />
                      @{creator.username}
                    </CardDescription>
                  </div>
                  <span className="text-xs font-bold px-2 py-1 bg-orange-100 text-orange-700 rounded-full">
                    {creator.category}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-600 line-clamp-2">{creator.description}</p>
                <p className="text-sm font-bold text-orange-600">{creator.followers} followers</p>
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(creator)}
                    className="flex-1"
                    data-testid={`edit-creator-${creator.id}`}
                  >
                    <Pencil size={14} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(creator.id)}
                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                    data-testid={`delete-creator-${creator.id}`}
                  >
                    <Trash2 size={14} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
